/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { weightSemiBold } from 'src/components/css'

export interface PanelStat {
  label: string
  value: string
  /** True when the underlying field is null — renders muted, not as a zero. */
  isUnset?: boolean
}

/**
 * The summary row under the panel title. Values are tabular so numbers line up
 * across stats.
 */
const PanelStatStrip = ({ stats }: { stats: PanelStat[] }) => {
  'use memo'

  if (stats.length === 0) {
    return null
  }

  return (
    <dl css={stripStyle}>
      {stats.map((stat) => (
        <div key={stat.label} css={statStyle}>
          <dt css={labelStyle}>{capitalizeWords(stat.label)}</dt>
          <dd
            css={valueStyle(Boolean(stat.isUnset))}
            data-unset={stat.isUnset ? 'true' : undefined}
          >
            {stat.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

const stripStyle = css`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 14px;
  padding: 0;
`

const statStyle = css`
  padding-right: 20px;
  margin-right: 20px;
  border-right: 1px solid ${colors.SECONDARY_300};

  &:last-of-type {
    border-right: none;
    margin-right: 0;
    padding-right: 0;
  }
`

const labelStyle = css`
  ${weightSemiBold}
  font-size: 14px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  margin-bottom: 4px;
`

const valueStyle = (isUnset: boolean) => css`
  ${weightSemiBold}
  margin: 0;
  /* Kept clear of the 14px label above it, so the value still reads as the
     dominant element rather than competing with its own heading. */
  font-size: ${isUnset ? '14px' : '20px'};
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  font-style: ${isUnset ? 'italic' : 'normal'};
  color: ${isUnset ? colors.NEUTRAL_100 : colors.NEUTRAL_0};
`

export default PanelStatStrip
